import React from "react";
import styled, { keyframes } from "styled-components";
import OpenButtonIcon from "../../../assets/open-button.svg";
import OpenLyricsIcon from "../../../assets/open-lyrics.svg";
import QuestionIcon from "../../../assets/question_icon.svg";
import RandomPromptIcon from "../../../assets/random-prompt.svg";
import CameraSVG from "../../../assets/camera.svg";
import PlayIcon from "../../../assets/play-player.svg";
import { listWorkspaces, Workspace, fetchWorkspaces, Track } from "../data/schema";
import { AudioSourceModal } from "./Modals/AudioSourceModal";
import { RecordAudioModal } from "./Modals/RecordAudioModal";
import { DeviceSize } from "constants/deviceSize";

type CustomProps = {
  openSections: { lyrics: boolean; styles: boolean; advanced: boolean };
  toggleSection: (section: 'lyrics' | 'styles' | 'advanced') => void;
  isLyricsExpanded: boolean;
  onToggleLyricsExpand: () => void;
  stylesDescription: string;
  setStylesDescription: (v: string) => void;
  availableStylesInspirations: string[];
  onStylesInspirationClick: (v: string) => void;
  vocalGender: 'male' | 'female' | '';
  setVocalGender: (v: 'male' | 'female' | '') => void;
  weirdness: number;
  styleInfluence: number;
  audioWeight: number;
  onSliderMouseDown: (e: React.MouseEvent, type: 'weirdness' | 'styleInfluence' | 'audioWeight') => void;
  onTrackClick: (e: React.MouseEvent, type: 'weirdness' | 'styleInfluence' | 'audioWeight') => void;
  showTooltip: { vocalGender: boolean; weirdness: boolean; styleInfluence: boolean; audioWeight: boolean };
  setShowTooltip: React.Dispatch<React.SetStateAction<{ vocalGender: boolean; weirdness: boolean; styleInfluence: boolean; audioWeight: boolean }>>;
  songTitle: string;
  setSongTitle: (v: string) => void;
  lyricsText: string;
  setLyricsText: (v: string) => void;
  saveWorkspaceId: string | null;
  setSaveWorkspaceId: (id: string) => void;
  hasPlayer?: boolean;
  onGenerateLyricsPrompt?: () => void;
  isGeneratingLyricsPrompt?: boolean;
  onGenerateStyles?: () => void;
  isGeneratingStyles?: boolean;
  stylesDisplayedCount?: number;
  stylesTotal?: number;
  lyricsTotal?: number;
  lyricsPromptMenu?: React.ReactNode;
  lyricsPromptMenuRef?: React.RefObject<HTMLDivElement | null>;
  onStylesScrollEnd?: () => void;
  showLyricsPromptButton?: boolean;
  styleMaxLength?: number;
  titleMaxLength?: number;
  lyricsMaxLength?: number;
  hasPersonas?: boolean;
  onPersonaClick?: () => void;
  selectedPersonaName?: string | null;
  selectedPersonaImageUrl?: string | null;
  onPersonaClear?: () => void;
  onPlayReferenceAudio?: (track: Track) => void;
  playingTrack?: Track | null;
  isPlaying?: boolean;
  onGlobalPlayerToggle?: () => void;
};

export const LeftPanelCustom: React.FC<CustomProps> = ({
  openSections,
  toggleSection,
  isLyricsExpanded,
  onToggleLyricsExpand,
  stylesDescription,
  setStylesDescription,
  availableStylesInspirations,
  onStylesInspirationClick,
  vocalGender,
  setVocalGender,
  weirdness,
  styleInfluence,
  audioWeight,
  onSliderMouseDown,
  onTrackClick,
  showTooltip,
  setShowTooltip,
  songTitle,
  setSongTitle,
  lyricsText,
  setLyricsText,
  saveWorkspaceId,
  setSaveWorkspaceId,
  hasPlayer = false,
  onGenerateLyricsPrompt,
  isGeneratingLyricsPrompt,
  onGenerateStyles,
  isGeneratingStyles,
  stylesDisplayedCount,
  stylesTotal,
  lyricsTotal,
  lyricsPromptMenu,
  lyricsPromptMenuRef,
  onStylesScrollEnd,
  showLyricsPromptButton = false,
  styleMaxLength,
  titleMaxLength,
  lyricsMaxLength,
  hasPersonas = false,
  onPersonaClick,
  selectedPersonaName,
  selectedPersonaImageUrl,
  onPersonaClear,
  onPlayReferenceAudio,
  playingTrack,
  isPlaying,
  onGlobalPlayerToggle,
}) => {
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [workspaces, setWorkspaces] = React.useState<Workspace[]>(() => listWorkspaces());
  const isMountedRef = React.useRef(true);
  const [isAudioModalOpen, setIsAudioModalOpen] = React.useState(false);
  const [isRecordModalOpen, setIsRecordModalOpen] = React.useState(false);
  const [referenceAudio, setReferenceAudio] = React.useState<{
    file: File;
    name: string;
    durationSec: number | null;
    objectUrl: string;
  } | null>(null);
  const referenceObjectUrlRef = React.useRef<string | null>(null);
  const [referenceCurrentTime, setReferenceCurrentTime] = React.useState<number>(0);
  const [referenceMode, setReferenceMode] = React.useState<'Cover' | 'Extend' | 'Add Vocals' | 'Add Instrumental'>('Extend');
  const [isExtendMenuOpen, setIsExtendMenuOpen] = React.useState(false);
  const extendMenuRef = React.useRef<HTMLDivElement | null>(null);

  const refreshWorkspaces = React.useCallback(
    async ({
      emitEvent = false,
      source = "custom-refresh",
      fetchRemote = true,
    }: { emitEvent?: boolean; source?: string; fetchRemote?: boolean } = {}) => {
      const local = listWorkspaces();
      if (isMountedRef.current) {
        setWorkspaces(local);
      }
      if (emitEvent) {
        window.dispatchEvent(
          new CustomEvent("workspaceListUpdated", { detail: { source } })
        );
      }

      if (fetchRemote) {
        try {
          const fresh = await fetchWorkspaces();
          if (isMountedRef.current) {
            setWorkspaces(fresh);
          }
          if (emitEvent) {
            window.dispatchEvent(
              new CustomEvent("workspaceListUpdated", { detail: { source } })
            );
          }
        } catch (error) {
          console.error("Unable to fetch workspaces", error);
        }
      }
    },
    []
  );

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    refreshWorkspaces({ emitEvent: true });
  }, [refreshWorkspaces]);

  React.useEffect(() => {
    const handleExternalUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ source?: string }>).detail;
      if (detail?.source === "custom-refresh") {
        return;
      }
      refreshWorkspaces({ emitEvent: false, fetchRemote: false });
    };
    window.addEventListener("workspaceListUpdated", handleExternalUpdate as EventListener);
    return () => {
      window.removeEventListener(
        "workspaceListUpdated",
        handleExternalUpdate as EventListener
      );
    };
  }, [refreshWorkspaces]);

  const handleReferenceAudioSelected = React.useCallback((file: File) => {
    if (referenceObjectUrlRef.current) {
      URL.revokeObjectURL(referenceObjectUrlRef.current);
      referenceObjectUrlRef.current = null;
    }

    const baseName = file.name.replace(/\.[^/.]+$/, "");
    const objectUrl = URL.createObjectURL(file);
    referenceObjectUrlRef.current = objectUrl;

    const audio = new Audio();
    audio.src = objectUrl;
    audio.addEventListener(
      "loadedmetadata",
      () => {
        const duration = Number.isFinite(audio.duration) ? audio.duration : null;
        setReferenceAudio({
          file,
          name: baseName,
          durationSec: duration,
          objectUrl,
        });
        setReferenceCurrentTime(0);
      },
      { once: true }
    );
    audio.addEventListener(
      "error",
      () => {
        setReferenceAudio({
          file,
          name: baseName,
          durationSec: null,
          objectUrl,
        });
      },
      { once: true }
    );
  }, []);

  React.useEffect(
    () => () => {
      if (referenceObjectUrlRef.current) {
        URL.revokeObjectURL(referenceObjectUrlRef.current);
        referenceObjectUrlRef.current = null;
      }
    },
    []
  );

  React.useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ localAudioUrl?: string; currentTime?: number }>).detail;
      if (!detail || !detail.localAudioUrl || typeof detail.currentTime !== 'number') return;
      if (!referenceAudio || !referenceAudio.objectUrl) return;
      if (detail.localAudioUrl !== referenceAudio.objectUrl) return;
      setReferenceCurrentTime(detail.currentTime);
    };
    window.addEventListener('localReferenceTimeUpdate', handler as EventListener);
    return () => window.removeEventListener('localReferenceTimeUpdate', handler as EventListener);
  }, [referenceAudio?.objectUrl]);

  const truncateName = (name: string) => {
    if (name.length <= 20) return name;
    return `${name.slice(0, 20)}...`;
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds == null || !Number.isFinite(seconds)) return "";
    const total = Math.round(seconds);
    const mins = Math.floor(total / 60)
      .toString()
      .padStart(2, "0");
    const secs = (total % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const filtered: Workspace[] = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter(w => w.name.toLowerCase().includes(q));
  }, [workspaces, search]);
  return (
    <CustomModeContainer $isLyricsExpanded={isLyricsExpanded}>
      {/* {hasPersonas && (
        <PersonaContainer $hasSelection={Boolean(selectedPersonaName)}>
          <PersonaAudioButton
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setIsAudioModalOpen(true);
            }}
          >
            + Audio
          </PersonaAudioButton>
          <PersonaMainButton
            type="button"
            onClick={onPersonaClick}
            $hasSelection={Boolean(selectedPersonaName)}
          >
            {selectedPersonaName ? (
              <PersonaButtonContent>
                <PersonaAvatar>
                  {selectedPersonaImageUrl ? (
                    <PersonaAvatarImg
                      src={selectedPersonaImageUrl}
                      alt={selectedPersonaName || "Persona"}
                    />
                  ) : (
                    <PersonaPlaceholderIcon />
                  )}
                </PersonaAvatar>
                <PersonaTextBlock>
                  <PersonaMeta>Persona</PersonaMeta>
                  <PersonaName>{selectedPersonaName}</PersonaName>
                </PersonaTextBlock>
              </PersonaButtonContent>
            ) : (
              <PersonaButtonLabel>+ Persona</PersonaButtonLabel>
            )}
          </PersonaMainButton>
          {onPersonaClear && selectedPersonaName && (
            <PersonaClearButton
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onPersonaClear();
              }}
              aria-label="Clear persona"
            >
              ×
            </PersonaClearButton>
          )}
        </PersonaContainer>
      )} */}
      {hasPersonas && (
        <PersonaContainer $hasSelection={Boolean(selectedPersonaName)}>
          
          <PersonaMainButton
            type="button"
            onClick={onPersonaClick}
            $hasSelection={Boolean(selectedPersonaName)}
          >
            
              <PersonaButtonLabel>+ Persona</PersonaButtonLabel>
            
          </PersonaMainButton>
          {onPersonaClear && selectedPersonaName && (
            <PersonaClearButton
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onPersonaClear();
              }}
              aria-label="Clear persona"
            >
              ×
            </PersonaClearButton>
          )}
        </PersonaContainer>
      )}
      {hasPersonas && referenceAudio && (
        <ReferenceAudioCard>
          <ReferenceAudioLeft>
            <ReferencePlayCircle
              onClick={(e) => {
                e.stopPropagation();
                if (!referenceAudio) return;
                const isThisLocalPlaying =
                  !!playingTrack &&
                  !!(playingTrack as any).params?.isLocalFile &&
                  (playingTrack as any).params?.localAudioUrl === referenceAudio.objectUrl;

                if (isThisLocalPlaying && onGlobalPlayerToggle) {
                  onGlobalPlayerToggle();
                  return;
                }

                if (!onPlayReferenceAudio) return;
                const now = Date.now();
                const localTrack: Track = {
                  id: `local-${now}`,
                  title: referenceAudio.name || 'Reference audio',
                  createdAt: now,
                  durationSec: referenceAudio.durationSec ?? undefined,
                  params: {
                    localAudioUrl: referenceAudio.objectUrl,
                    isLocalFile: true,
                  },
                };
                onPlayReferenceAudio(localTrack);
              }}
            >
              {playingTrack &&
              (playingTrack as any).params?.isLocalFile &&
              (playingTrack as any).params?.localAudioUrl === referenceAudio.objectUrl &&
              isPlaying ? (
                <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill:'#6777FF'  }}>
                <rect x="6" y="4" width="4" height="16" fill='#6777FF' />
                <rect x="14" y="4" width="4" height="16" fill='#6777FF' />
              </svg>
              ) : (
                <ReferencePlayIcon />
              )}
            </ReferencePlayCircle>
            <ReferenceTextBlock>
              <ReferenceTitle title={referenceAudio.name}>
                {truncateName(referenceAudio.name)}
              </ReferenceTitle>
              {referenceAudio.durationSec != null && (
                <ReferenceDuration>
                  {`${formatDuration(referenceCurrentTime)} / ${formatDuration(referenceAudio.durationSec)}`}
                </ReferenceDuration>
              )}
            </ReferenceTextBlock>
          </ReferenceAudioLeft>
          <ReferenceActions>
            <ReferenceMenuWrapper ref={extendMenuRef}>
              <ReferenceMenuButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExtendMenuOpen((prev) => !prev);
                }}
              >
                {referenceMode}
                <ReferenceMenuCaret>▾</ReferenceMenuCaret>
              </ReferenceMenuButton>
              {isExtendMenuOpen && (
                <ReferenceMenuDropdown onClick={(e) => e.stopPropagation()}>
                  <ReferenceMenuItem
                    onClick={() => {
                      setReferenceMode('Cover');
                      setIsExtendMenuOpen(false);
                    }}
                  >
                    Cover
                  </ReferenceMenuItem>
                  <ReferenceMenuItem
                    onClick={() => {
                      setReferenceMode('Extend');
                      setIsExtendMenuOpen(false);
                    }}
                  >
                    Extend
                  </ReferenceMenuItem>
                  <ReferenceMenuItem
                    onClick={() => {
                      setReferenceMode('Add Vocals');
                      setIsExtendMenuOpen(false);
                    }}
                  >
                    Add Vocals
                  </ReferenceMenuItem>
                  <ReferenceMenuItem
                    onClick={() => {
                      setReferenceMode('Add Instrumental');
                      setIsExtendMenuOpen(false);
                    }}
                  >
                    Add Instrumental
                  </ReferenceMenuItem>
                </ReferenceMenuDropdown>
              )}
            </ReferenceMenuWrapper>
          </ReferenceActions>
        </ReferenceAudioCard>
      )}

      <ExpandedLyricsContainer $isExpanded={isLyricsExpanded} $hasPersonas={hasPersonas}>
        <ExpandedLyricsHeader onClick={() => toggleSection('lyrics')}>
          <CustomSectionLeft>
            <StyledOpenButtonIcon $isOpen={openSections.lyrics}>
              <OpenButtonIcon />
            </StyledOpenButtonIcon>
            <CustomSectionText>
              Lyrics
              
            </CustomSectionText>
          </CustomSectionLeft>
          <HeaderActions>
            {showLyricsPromptButton && (
              <ActionWithMenu ref={lyricsPromptMenuRef || undefined}>
                <IconButton
                  type="button"
                  disabled={isGeneratingLyricsPrompt}
            onClick={(e) => {
              e.stopPropagation();
                    if (isGeneratingLyricsPrompt) return;
                    onGenerateLyricsPrompt?.();
            }}
          >
                  {isGeneratingLyricsPrompt ? <IconSpinner /> : <StyledRandomPromptIcon />}
                </IconButton>
                {lyricsPromptMenu}
              </ActionWithMenu>
            )}
          </HeaderActions>
        </ExpandedLyricsHeader>
        
        <LyricsContentWrapper 
          $isOpen={openSections.lyrics} 
          $isExpanded={isLyricsExpanded}
        >
          <LyricsTextarea 
            placeholder="Write your lyrics here (skip if it's instrumental)" 
            $isExpanded={isLyricsExpanded}
            $hasPlayer={hasPlayer}
            value={lyricsText}
            onChange={(e) => setLyricsText(e.target.value)}
            maxLength={lyricsMaxLength}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          />
          <OpenLyricsButton onClick={(e) => {
            e.stopPropagation();
            onToggleLyricsExpand();
          }}>
            <OpenLyricsIcon />
          </OpenLyricsButton>
        </LyricsContentWrapper>
      </ExpandedLyricsContainer>
      
      <CustomModeContent $isExpanded={isLyricsExpanded}>
       
        <ExpandedStylesContainer>
          <ExpandedStylesHeader onClick={() => toggleSection('styles')}>
            <CustomSectionLeft>
              <StyledOpenButtonIcon $isOpen={openSections.styles}>
                <OpenButtonIcon />
              </StyledOpenButtonIcon>
              <CustomSectionText>
                Styles
                
              </CustomSectionText>
            </CustomSectionLeft>
            <HeaderActions>
              
            </HeaderActions>
          </ExpandedStylesHeader>
          
          <StylesContentWrapper $isOpen={openSections.styles}>
            <StylesTextarea 
              placeholder="Describe the style or genre"
              value={stylesDescription}
              onChange={(e) => setStylesDescription(e.target.value)}
              maxLength={styleMaxLength}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            />
            
            <InspirationSection>
              <InspirationScrollContainer
                onScroll={(event) => {
                  const target = event.currentTarget;
                  if (target.scrollWidth - target.scrollLeft - target.clientWidth <= 24) {
                    onStylesScrollEnd?.();
                  }
                }}
              >
                {availableStylesInspirations.map((inspiration) => (
                  <InspirationButton 
                    key={inspiration}
                    onClick={() => onStylesInspirationClick(inspiration)}
                  >
                    + {inspiration}
                  </InspirationButton>
                ))}
              </InspirationScrollContainer>
            </InspirationSection>
          </StylesContentWrapper>
        </ExpandedStylesContainer>
          
        <AdvancedOptionContainer>
          <AdvancedOptionHeader 
            onClick={() => toggleSection('advanced')}
            $isOpen={openSections.advanced}
          >
            <CustomSectionLeft>
              <StyledOpenButtonIcon $isOpen={openSections.advanced}>
                <OpenButtonIcon />
              </StyledOpenButtonIcon>
              <CustomSectionText>
                Advanced Option
                
                </CustomSectionText>
            </CustomSectionLeft>
          </AdvancedOptionHeader>
          
          <AdvancedOptionContent $isOpen={openSections.advanced}>
            {/* Vocal Gender */}
            <AdvancedControlRow>
              <AdvancedControlLabel>
                Vocal Gender
                <QuestionIconWrapper
                  onMouseEnter={() => setShowTooltip(prev => ({ ...prev, vocalGender: true }))}
                  onMouseLeave={() => setShowTooltip(prev => ({ ...prev, vocalGender: false }))}
                >
                  <QuestionIcon />
                  {showTooltip.vocalGender && (
                    <Tooltip>
                      Change the gender of the generated vocals
                    </Tooltip>
                  )}
                </QuestionIconWrapper>
              </AdvancedControlLabel>
              <GenderButtonGroup>
                <GenderButton 
                  $active={vocalGender === 'male'} 
                  onClick={() => setVocalGender(vocalGender === 'male' ? '' : 'male')}
                >
                  Male
                </GenderButton>
                <GenderButton 
                  $active={vocalGender === 'female'} 
                  onClick={() => setVocalGender(vocalGender === 'female' ? '' : 'female')}
                >
                  Female
                </GenderButton>
              </GenderButtonGroup>
            </AdvancedControlRow>
            
            {/* Weirdness */}
            <AdvancedControlRow>
              <AdvancedControlLabel>
                Weirdness
                <QuestionIconWrapper
                  onMouseEnter={() => setShowTooltip(prev => ({ ...prev, weirdness: true }))}
                  onMouseLeave={() => setShowTooltip(prev => ({ ...prev, weirdness: false }))}
                >
                  <QuestionIcon />
                  {showTooltip.weirdness && (
                    <Tooltip>
                      Turn it up for wild, unexpected results
                    </Tooltip>
                  )}
                </QuestionIconWrapper>
              </AdvancedControlLabel>
              <SliderContainer>
                <SliderTrack 
                  data-slider-track
                  onClick={(e) => onTrackClick(e, 'weirdness')}
                >
                  <SliderTrackInner>
                    {Array.from({ length: 11 }, (_, i) => (
                      <SliderTick key={i} />
                    ))}
                  </SliderTrackInner>
                  <SliderThumb 
                    $position={weirdness}
                    onMouseDown={(e) => onSliderMouseDown(e, 'weirdness')}
                  />
                </SliderTrack>
                <SliderValue>{weirdness}%</SliderValue>
              </SliderContainer>
            </AdvancedControlRow>
            
            {/* Style Influence */}
            <AdvancedControlRow>
              <AdvancedControlLabel>
                Style Influence
                <QuestionIconWrapper
                  onMouseEnter={() => setShowTooltip(prev => ({ ...prev, styleInfluence: true }))}
                  onMouseLeave={() => setShowTooltip(prev => ({ ...prev, styleInfluence: false }))}
                >
                  <QuestionIcon />
                  {showTooltip.styleInfluence && (
                    <Tooltip>
                      Turn it up to match your style description
                    </Tooltip>
                  )}
                </QuestionIconWrapper>
              </AdvancedControlLabel>
              <SliderContainer>
                <SliderTrack 
                  data-slider-track
                  onClick={(e) => onTrackClick(e, 'styleInfluence')}
                >
                  <SliderTrackInner>
                    {Array.from({ length: 11 }, (_, i) => (
                      <SliderTick key={i} />
                    ))}
                  </SliderTrackInner>
                  <SliderThumb 
                    $position={styleInfluence}
                    onMouseDown={(e) => onSliderMouseDown(e, 'styleInfluence')}
                  />
                </SliderTrack>
                <SliderValue>{styleInfluence}%</SliderValue>
              </SliderContainer>
            </AdvancedControlRow>

            {/* Audio Weight */}
            <AdvancedControlRow>
              <AdvancedControlLabel>
                Audio Weight
                <QuestionIconWrapper
                  onMouseEnter={() => setShowTooltip(prev => ({ ...prev, audioWeight: true }))}
                  onMouseLeave={() => setShowTooltip(prev => ({ ...prev, audioWeight: false }))}
                >
                  <QuestionIcon />
                  {showTooltip.audioWeight && (
                    <Tooltip>
                      Blend uploaded audio with the generated track
                    </Tooltip>
                  )}
                </QuestionIconWrapper>
              </AdvancedControlLabel>
              <SliderContainer>
                <SliderTrack
                  data-slider-track
                  onClick={(e) => onTrackClick(e, 'audioWeight')}
                >
                  <SliderTrackInner>
                    {Array.from({ length: 11 }, (_, i) => (
                      <SliderTick key={i} />
                    ))}
                  </SliderTrackInner>
                  <SliderThumb
                    $position={audioWeight}
                    onMouseDown={(e) => onSliderMouseDown(e, 'audioWeight')}
                  />
                </SliderTrack>
                <SliderValue>{audioWeight}%</SliderValue>
              </SliderContainer>
            </AdvancedControlRow>
          </AdvancedOptionContent>
        </AdvancedOptionContainer>
        
        <CustomTitleInput
          placeholder="Add a song title"
          value={songTitle}
          onChange={(e) => setSongTitle(e.target.value)}
          maxLength={titleMaxLength}
          $hasValue={songTitle.length > 0}
        />
        <SaveRow>
          <SaveLeft>Save to...</SaveLeft>
          <SaveRight>
            <SavePill
              onClick={(e) => {
                e.stopPropagation();
                const willOpen = !saveOpen;
                setSaveOpen(willOpen);
                if (willOpen) {
                  refreshWorkspaces({ emitEvent: false, fetchRemote: true });
                }
              }}
            >
              {(() => {
                const ws = saveWorkspaceId
                  ? workspaces.find(item => item.id === saveWorkspaceId) || null
                  : workspaces[0] || null;
                const name = ws ? ws.name : 'No workspace';
                return name.length > 15 ? `${name.slice(0, 15)}...` : name;
              })()}
            </SavePill>
            {saveOpen && (
              <SaveDropdown $openUp onClick={(e) => e.stopPropagation()}>
                <SaveSearch
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <SaveList>
                  {filtered.map((ws: Workspace) => {
                    const count = ws.trackIds.length;
                    const selected = saveWorkspaceId === ws.id;
                    return (
                      <SaveItem
                        key={ws.id}
                        $selected={selected}
                        onMouseDown={() => {
                          setSaveWorkspaceId(ws.id);
                          setSaveOpen(false);
                          try {
                            localStorage.setItem('aiCompose.saveWorkspaceId', ws.id);
                            localStorage.setItem('aiCompose.lastWorkspaceId', ws.id);
                          } catch {}
                          window.dispatchEvent(new CustomEvent('workspaceSelected', { detail: { workspaceId: ws.id } }));
                        }}
                      >
                        <span>{ws.name} ({count} {count === 1 ? 'song' : 'songs'})</span>
                      </SaveItem>
                    );
                  })}
                </SaveList>
              </SaveDropdown>
            )}
          </SaveRight>
        </SaveRow>
      </CustomModeContent>
      <AudioSourceModal
        open={isAudioModalOpen}
        onClose={() => setIsAudioModalOpen(false)}
        onAudioUploaded={(file) => {
          handleReferenceAudioSelected(file);
        }}
        onRecordRequested={() => {
          setIsAudioModalOpen(false);
          setIsRecordModalOpen(true);
        }}
      />
      <RecordAudioModal
        open={isRecordModalOpen}
        onClose={() => setIsRecordModalOpen(false)}
        onRecorded={(file) => {
          handleReferenceAudioSelected(file);
        }}
      />
    </CustomModeContainer>
  );
};

const InspirationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 0;
  max-width: 500px;
`;

const InspirationScrollContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 10px 8px 20px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 2px;
    margin: 0 20px 0 20px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0a0a0;
  }
`;

const InspirationButton = styled.button`
  padding: 8px 12px;
  border-radius: 16px;
  border: none;
  background-color: #CFD3FF;
  color: #000;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 220px;
  white-space: normal;
  text-align: left;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-shrink: 0;
  
  &:hover {
    background-color: #b8bef5;
  }
`;

// Стили для Custom режима
const CustomModeContainer = styled.div<{ $isLyricsExpanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  flex: 1;
  overflow-y: ${props => props.$isLyricsExpanded ? 'hidden' : 'auto'};
  min-height: 0;
  max-width: 75%;
  @media (max-width: ${DeviceSize.TabletMedium}) {
   width: 90vw;
  }
`;

const CustomModeContent = styled.div<{ $isExpanded: boolean }>`
  display: ${props => props.$isExpanded ? 'none' : 'flex'};
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
`;
const CustomTitleInput = styled.input<{ $hasValue: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${props => props.$hasValue ? '#000' : 'rgb(215, 215, 215)'};
  background-color: white;
  color: #000;
  font-size: 14px;
  outline: none;
  flex-shrink: 0;
  transition: border-color 0.2s ease;
  
  &::placeholder {
    color: #999;
  }
`;
const SaveRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  border: 1px solid rgb(215, 215, 215);
  border-radius: 12px;
  background: #fff;
`;

const SaveLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #000;
  font-size: 14px;
`;

const SaveRight = styled.div`
  position: relative;
`;

const SavePill = styled.button`
  padding: 8px 14px;
  border-radius: 18px;
  border: 1px solid rgb(215, 215, 215);
  background: #fff;
  color: #333;
  cursor: pointer;
`;

const SaveDropdown = styled.div<{ $openUp?: boolean }>`
  position: absolute;
  ${p => p.$openUp ? 'bottom: calc(100% + 8px);' : 'top: calc(100% + 8px);'}
  right: 0;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
  padding: 8px;
  width: 280px;
  z-index: 30;
`;

const SaveSearch = styled.input`
  width: 100%;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 0 10px;
  outline: none;
  margin-bottom: 8px;
`;

const SaveList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SaveItem = styled.button<{ $selected?: boolean }>`
  text-align: left;
  border: none;
  background: ${p => p.$selected ? '#F4F4F4' : '#fff'};
  box-shadow: ${p => p.$selected ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'};
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  color: #000;
  &:hover {
    background: ${p => p.$selected ? '#e9e9e9' : '#F4F4F4'};
    box-shadow: ${p => p.$selected ? '0 2px 4px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)'};
  }
`;

const PersonaContainer = styled.div<{ $hasSelection?: boolean }>`
  margin-top: 20px;
  width: 100%;
  border-radius: 40px;
  border: 1px solid #B0B8FF;
  background-color: ${p => (p.$hasSelection ? "#f8f9ff" : "#ffffff")};
  display: flex;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`;

const PersonaAudioButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: #ffffff;
  color: rgb(106, 121, 255);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #B0B8FF;
`;

const PersonaMainButton = styled.button<{ $hasSelection?: boolean }>`
  flex: 1;
  padding: ${p => (p.$hasSelection ? '4px 16px' : '12px 16px')};
  border: none;
  background-color: ${p => (p.$hasSelection ? "#f8f9ff" : "#ffffff")};
  color: ${p => (p.$hasSelection ? "#111" : "rgb(106, 121, 255)")};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  letter-spacing: 0.2px;
`;

const PersonaButtonLabel = styled.span`
  font-weight: 600;
  color: rgb(106, 121, 255);
`;

const PersonaButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PersonaAvatar = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e8eaff, #f4f4f4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(103, 119, 255, 0.25);
`;

const PersonaAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const PersonaPlaceholderIcon = styled(CameraSVG)`
  width: 22px;
  height: 22px;
  stroke: #6777ff;
  fill: none;
`;

const PersonaTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

const PersonaMeta = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6c757d;
`;

const PersonaName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PersonaClearButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(103, 119, 255, 0.12);
  color: #6777ff;
  font-size: 15px;
  line-height: 20px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(103, 119, 255, 0.2);
  }
`;

const ReferenceAudioCard = styled.div`
  width: 100%;
  border-radius: 18px;
  background: #ffffff;
  color: #111827;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
`;

const ReferenceAudioLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const ReferencePlayCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ReferencePlayIcon = styled(PlayIcon)`
  width: 20px;
  height: 20px;
`;

const ReferenceTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ReferenceTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReferenceDuration = styled.div`
  font-size: 11px;
  color: #6b7280;
`;

const ReferenceActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: 12px;
`;

const ReferenceMenuWrapper = styled.div`
  position: relative;
`;

const ReferenceMenuButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  font-size: 13px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const ReferenceMenuCaret = styled.span`
  font-size: 11px;
  color: #6b7280;
`;

const ReferenceMenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  border-radius: 12px;
  background: #ffffff;
  color: #111827;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  padding: 4px 0;
  z-index: 20;
`;

const ReferenceMenuItem = styled.button`
  width: 100%;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

const CustomSectionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledOpenButtonIcon = styled.div<{ $isOpen: boolean }>`
  width: 10px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
  
  svg {
    width: 10px;
    height: 12px;
  }
`;

const CustomSectionText = styled.span`
  color: #000;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
`;

const ExpandedStylesContainer = styled.div`
  width: 100%;
  background-color: #F4F4F4;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const ExpandedStylesHeader = styled.div`
  width: 100%;
  padding: 16px 16px 12px 16px;
  background-color: #F4F4F4;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  
  &:hover {
    background-color: #F4F4F4;
  }
  
  &:focus {
    outline: none;
  }
`;

const StylesContentWrapper = styled.div<{ $isOpen: boolean }>`
  position: relative;
  width: 100%;
  max-height: ${props => props.$isOpen ? '24vh' : '0px'};
  overflow: hidden;
  transition: max-height 0.2s cubic-bezier(0.2, 0.1, 0.01, 1);
  padding: ${props => props.$isOpen ? '0 0 1.4vh 0' : '0'}
  @media (max-width: ${DeviceSize.TabletMedium}) {
     padding: ${props => props.$isOpen ? '0 0 0.3vh 0' : '0'}

  }
`;

const StylesTextarea = styled.textarea`
  width: 100%;
  min-height: 14vh;
  padding: 0;
  border: none;
  border-radius: 0;
  background-color: transparent;
  color: #000;
  font-size: 14px;
  outline: none;
  resize: none;
  font-family: inherit;
  cursor: text;
  margin-bottom: 1.8vh;
  
  &::placeholder {
    color: #999;
  }
  padding: 0 16px 1.4vh 16px;

`;

const ExpandedLyricsContainer = styled.div<{ $isExpanded: boolean, $hasPersonas: boolean }>`
${props => props.$hasPersonas ? `
  margin-top: 0px;

` : `
margin-top: 12px;
`}
  width: 100%;
  background-color: #F4F4F4;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  ${props => props.$isExpanded ? `
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    margin-bottom: 0;
  ` : `
    flex-shrink: 0;
  `}
`;

const ExpandedLyricsHeader = styled.div`
  width: 100%;
  padding: 16px 16px 12px 16px;
  background-color: #F4F4F4;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
`;

const LyricsContentWrapper = styled.div<{ $isOpen: boolean; $isExpanded: boolean }>`
  position: relative;
  width: 100%;
  ${props => {
    if (!props.$isOpen) {
      return `
        height: 0px;
        overflow: hidden;
      `;
    }
    if (props.$isExpanded) {
      return `
        flex: 1;
        min-height: 0;
        overflow: auto;
        display: flex;
        flex-direction: column;
      `;
    }
    return `
      height: 18vh;
      overflow: hidden;
    `;
  }}
  transition: height 0.2s cubic-bezier(0.2, 0.1, 0.01, 1);
  padding: ${props => props.$isOpen ? '0 16px 12px 16px' : '0 16px'};
`;

const LyricsTextarea = styled.textarea<{ $isExpanded: boolean; $hasPlayer?: boolean }>`
  width: 100%;
  ${props => props.$isExpanded ? `
    flex: 1;
    min-height: ${props.$hasPlayer ? '35vh' : '47vh'};
  ` : `
    min-height: 14vh;
  `}
  padding: 0;
  border: none;
  border-radius: 0;
  background-color: transparent;
  color: #000;
  font-size: 14px;
  outline: none;
  resize: none;
  font-family: inherit;
  cursor: text;
  transition: min-height 0.2s cubic-bezier(0.2, 0.1, 0.01, 1);
  &::placeholder {
    color: #999;
  }
`;

const OpenLyricsButton = styled.button`
  position: absolute;
  bottom: 1.4vh;
  right: 1.4vh;
  width: 3.4vh;
  height: 3.4vh;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 1;
  transform: translateY(0);
  transition: all 0.4s cubic-bezier(0.4, 0.3, 0.2, 1);
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SectionContent = styled.div`
  width: 100%;
  padding: 1.4vh 16px;
  border-radius: 8px;
  border: 1px solid #F4F4F4;
  background-color: white;
  margin-bottom: 1.4vh;
`;

const SectionPlaceholder = styled.div`
  color: #999;
  font-size: 14px;
`;



// Advanced Option Styles
const AdvancedOptionContainer = styled.div`
  width: 100%;
  background-color: #F4F4F4;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: visible;
  flex-shrink: 0;
`;

const AdvancedOptionHeader = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: 16px 16px 12px 16px;
  background-color: #F4F4F4;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-align: left;
  border-radius: ${props => props.$isOpen ? '8px 8px 0 0' : '8px'};
  
  &:hover {
    background-color: #F4F4F4;
  }
  
  &:focus {
    outline: none;
  }
`;

const AdvancedOptionContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '60vh' : '0px'};
  overflow: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: max-height 0.2s cubic-bezier(0.2, 0.1, 0.01, 1);
  padding: ${props => props.$isOpen ? '0 16px 2vh 16px' : '0 16px'};
  border-radius: ${props => props.$isOpen ? '0 0 8px 8px' : '0'};
`;

const AdvancedControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.4vh;
  padding: 2vh;
  background-color: #E2E5FF;
  border-radius: 12px;
  overflow: visible;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AdvancedControlLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  height: 3.6vh;
`;

const QuestionIconWrapper = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
`;

const Tooltip = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background-color: #6777FF;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-bottom-color: #6777FF;
  }
`;

const GenderButtonGroup = styled.div`
  display: flex;
  gap: 0;
  border-radius: 20px;
  padding: 2px;
  background-color: #E2E5FF;
`;

const GenderButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border-radius: 18px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  
  ${props => props.$active ? `
    background: linear-gradient(135deg, #B7BFFF 0%, #6777FF 100%);
    color: white;
  ` : `
    background: #E2E5FF;
    color: #6c757d;
  `}
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 2.4vh;
  background-color: transparent;
  border-radius: 2px;
  cursor: pointer;
  padding: 0 8px;
  display: flex;
  align-items: center;
`;

const SliderTrackInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SliderTick = styled.div`
  width: 1px;
  height: 2.4vh;
  background-color: #666;
  position: relative;
  z-index: 1;
`;

const SliderThumb = styled.div<{ $position: number }>`
  position: absolute;
  top: 50%;
  left: calc(8px + (100% - 16px) * ${props => props.$position} / 100);
  transform: translate(-50%, -50%);
  width: 0.9vh;
  height: 2.9vh;
  background: linear-gradient(135deg, #7987FF 0%, #FF7B7D 100%);
  border-radius: 12px;
  cursor: grab;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  
  &:active {
    cursor: grabbing;
  }
`;

const SliderValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  min-width: 40px;
  text-align: right;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#dfe3ff' : '#CFD3FF'};
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.7 : 1};

  &:hover {
    background-color: ${props => props.disabled ? '#dfe3ff' : '#b8bef5'};
  }
 @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 40px;
    height: 40px;
  }
 
`;

const StyledRandomPromptIcon = styled(RandomPromptIcon)`
  width: 15px;
  height: 15px;
  display: block;
 
`;

const CountBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6777FF;
  background: rgba(103, 119, 255, 0.15);
  border-radius: 12px;
  padding: 2px 8px;
  margin-left: 8px;
`;

const ActionWithMenu = styled.div`
  position: relative;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const IconSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(103, 119, 255, 0.3);
  border-top-color: #6777FF;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export default LeftPanelCustom;


